/**
 * @file backup.component.ts
 * @description Standalone Angular 22 component for database backups (JSON ZIP, Excel) and rollback restoration.
 */

import { Component, OnDestroy, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  BackupService,
  DehydrationAnalysis,
  DehydrationCollectionImpact,
} from '../../services/backup.service';
import {
  BackupSummary,
  RollbackDryRunReport,
  RollbackResultItem,
} from '../../interfaces/backup.interfaces';

@Component({
  selector: 'app-backup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './backup.component.html',
  styleUrl: './backup.component.scss',
})
export class BackupComponent implements OnInit, OnDestroy {
  /** Duración en ms del aviso temporal sobre el botón cuando no existen fechas elegibles. */
  static EMPTY_NOTICE_DURATION_MS = 3500;

  private readonly backupService = inject(BackupService);
  private readonly cdr = inject(ChangeDetectorRef);
  private emptyNoticeTimer: ReturnType<typeof setTimeout> | null = null;

  // General state
  isLoading = true;
  summary: BackupSummary | null = null;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  // Export action state
  isDownloadingJson = false;
  isDownloadingExcel = false;

  // Rollback file upload & dry-run state
  selectedFile: File | null = null;
  isValidatingRollback = false;
  isExecutingRollback = false;
  dryRunReport: RollbackDryRunReport | null = null;
  restoreResults: RollbackResultItem[] | null = null;

  // Confirmation modal state
  showConfirmModal = false;

  // --------------------------------------------------------------------------
  // Rehidratación de cortes históricos
  // --------------------------------------------------------------------------
  selectedDehydrationFile: File | null = null;
  isValidatingDehydrationRestore = false;
  isExecutingDehydrationRestore = false;
  dehydrationDryRunReport: any | null = null;
  dehydrationRestoreResult: any | null = null;
  showDehydrationConfirmModal = false;

  // --------------------------------------------------------------------------
  // Deshidratación / corte de registros (Fase D.2 - RA4-1)
  // --------------------------------------------------------------------------
  /** Estado del botón Analizar (refleja el resultado directamente en el propio botón). */
  analysisState: 'idle' | 'analyzing' | 'found' | 'empty' = 'idle';
  /** Lista de fechas de corte disponibles con impacto precalculado. */
  availableCutoffs: DehydrationAnalysis[] = [];
  /** Fecha de corte ISO seleccionada por el administrador desde el dropdown. */
  cutoffDate = '';
  /** Resumen de impacto asociado a la fecha elegida; null hasta elegir en el dropdown. */
  dehydrationAnalysis: DehydrationAnalysis | null = null;
  /** Token de un solo uso emitido por download; habilita el purge. */
  purgeToken: string | null = null;
  isAnalyzingDehydration = false;
  isDownloadingDehydration = false;
  isPurgingDehydration = false;
  /** Modal de confirmación para el borrado definitivo del corte. */
  showPurgeConfirmModal = false;

  /** Colecciones afectadas con al menos 1 registro en el corte seleccionado. */
  get impactCollections(): DehydrationCollectionImpact[] {
    return (this.dehydrationAnalysis?.collections ?? []).filter(
      (col) => (col.matchedCount ?? col.purgedCount ?? 0) > 0,
    );
  }

  ngOnInit(): void {
    // IMPORTANTE (RA4-1): aquí SOLO se carga el estado de la BD.
    // El análisis de deshidratación NO se dispara en ngOnInit.
    this.loadStatus();
  }

  ngOnDestroy(): void {
    this.clearEmptyNoticeTimer();
  }

  private clearEmptyNoticeTimer(): void {
    if (this.emptyNoticeTimer !== null) {
      clearTimeout(this.emptyNoticeTimer);
      this.emptyNoticeTimer = null;
    }
  }

  /**
   * Loads current database status and collection metrics.
   */
  loadStatus(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.backupService.getStatus().subscribe({
      next: (summary) => {
        this.summary = summary;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.errorMessage =
          err?.error?.message ||
          'No fue posible obtener el estado de la base de datos. Verifica la conexión con el servidor.';
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Initiates download of the compressed JSON ZIP backup.
   */
  downloadJsonBackup(): void {
    this.isDownloadingJson = true;
    this.errorMessage = null;

    const timestamp = new Date()
      .toISOString()
      .replace(/T/, '_')
      .replace(/:/g, '-')
      .split('.')[0];
    const filename = `abaq-backup-${timestamp}.zip`;

    this.backupService.downloadJsonBackup().subscribe({
      next: (blob) => {
        this.backupService.triggerDownload(blob, filename);
        this.isDownloadingJson = false;
        this.showSuccess('Respaldo en JSON comprimido descargado correctamente.');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isDownloadingJson = false;
        this.errorMessage =
          err?.error?.message || 'Error al generar la descarga del respaldo JSON.';
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Initiates download of the styled Excel workbook.
   */
  downloadExcelBackup(): void {
    this.isDownloadingExcel = true;
    this.errorMessage = null;

    const timestamp = new Date()
      .toISOString()
      .replace(/T/, '_')
      .replace(/:/g, '-')
      .split('.')[0];
    const filename = `abaq-database-${timestamp}.xlsx`;

    this.backupService.downloadExcelBackup().subscribe({
      next: (blob) => {
        this.backupService.triggerDownload(blob, filename);
        this.isDownloadingExcel = false;
        this.showSuccess('Copia en formato Excel (.xlsx) descargada correctamente.');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isDownloadingExcel = false;
        this.errorMessage =
          err?.error?.message || 'Error al generar el libro de Excel de la base de datos.';
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * File input change handler for rollback ZIP selection.
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (!file.name.endsWith('.zip')) {
        this.errorMessage = 'El archivo de respaldo debe ser un archivo comprimido .zip.';
        this.selectedFile = null;
        input.value = '';
        return;
      }
      this.selectedFile = file;
      this.dryRunReport = null;
      this.restoreResults = null;
      this.errorMessage = null;
    }
  }

  /**
   * Clears currently selected rollback file and report.
   */
  clearSelectedFile(): void {
    this.selectedFile = null;
    this.dryRunReport = null;
    this.restoreResults = null;
    this.errorMessage = null;
  }

  /**
   * Runs dry-run validation on the uploaded backup ZIP without altering database records.
   */
  validateRollback(): void {
    if (!this.selectedFile) return;

    this.isValidatingRollback = true;
    this.errorMessage = null;

    this.backupService.restoreBackup(this.selectedFile, false).subscribe({
      next: (res) => {
        this.isValidatingRollback = false;
        if (res.report) {
          this.dryRunReport = res.report;
          this.showSuccess('Validación de integridad completada. Revisa los datos antes de continuar.');
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isValidatingRollback = false;
        this.errorMessage =
          err?.error?.message || 'El archivo de respaldo no pasó la validación de integridad.';
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Opens the confirmation modal before final database rollback.
   */
  openConfirmModal(): void {
    this.showConfirmModal = true;
  }

  /**
   * Closes the confirmation modal.
   */
  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }

  /**
   * Executes the final rollback restoration.
   */
  executeRollback(): void {
    if (!this.selectedFile) return;

    this.showConfirmModal = false;
    this.isExecutingRollback = true;
    this.errorMessage = null;

    this.backupService.restoreBackup(this.selectedFile, true).subscribe({
      next: (res) => {
        this.isExecutingRollback = false;
        this.restoreResults = res.results || [];
        this.dryRunReport = null;
        this.selectedFile = null;
        this.showSuccess('¡Rollback completado exitosamente! La base de datos ha sido restaurada.');
        this.loadStatus();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isExecutingRollback = false;
        this.errorMessage =
          err?.error?.message || 'Ocurrió un error al ejecutar la restauración de la base de datos.';
        this.cdr.detectChanges();
      },
    });
  }

  /* ========================================================================
     DESHIDRATACIÓN / CORTE DE REGISTROS (Fase D.2 - RA4-1)
     ======================================================================== */

  /**
   * Paso 1: Analiza el sistema bajo demanda para descubrir fechas de corte
   * disponibles. Solo se ejecuta al pulsar 'Analizar' (NUNCA en ngOnInit).
   * Refleja el resultado directamente en el estado del propio botón.
   */
  analyzeDehydration(): void {
    if (
      this.isAnalyzingDehydration ||
      this.isDownloadingDehydration ||
      this.isPurgingDehydration
    ) {
      return;
    }

    this.clearEmptyNoticeTimer();
    this.isAnalyzingDehydration = true;
    this.analysisState = 'analyzing';
    this.errorMessage = null;
    this.cutoffDate = '';
    this.dehydrationAnalysis = null;
    this.availableCutoffs = [];
    // Un nuevo análisis invalida cualquier token previo.
    this.purgeToken = null;

    this.backupService.discoverDehydrationCutoffs().subscribe({
      next: (res) => {
        this.availableCutoffs = res.availableCutoffs ?? [];
        this.isAnalyzingDehydration = false;
        if (this.availableCutoffs.length > 0) {
          this.analysisState = 'found';
        } else {
          this.analysisState = 'empty';
          this.emptyNoticeTimer = setTimeout(() => {
            this.emptyNoticeTimer = null;
            if (this.analysisState === 'empty') {
              this.analysisState = 'idle';
              this.cdr.detectChanges();
            }
          }, BackupComponent.EMPTY_NOTICE_DURATION_MS);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.clearEmptyNoticeTimer();
        this.isAnalyzingDehydration = false;
        this.analysisState = 'idle';
        this.availableCutoffs = [];
        this.dehydrationAnalysis = null;
        this.errorMessage =
          err?.error?.message || 'No fue posible analizar el sistema en busca de fechas de corte.';
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Paso 2: Handler del dropdown de fecha de corte.
   * GARANTÍA RA4-1: cambiar de opción NO lanza consultas al servidor; asigna en
   * memoria el resumen precalculado e invalida cualquier purgeToken previo.
   */
  onCutoffDateChange(): void {
    this.purgeToken = null;
    this.errorMessage = null;
    this.dehydrationAnalysis =
      this.availableCutoffs.find((item) => item.cutoffDate === this.cutoffDate) ?? null;
  }

  /** Formatea una fecha ISO/YYYY-MM-DD como dd/mm/yyyy en UTC sin desfase horario. */
  formatCutoffDateDisplay(isoOrKey: string): string {
    if (!isoOrKey) return '';
    const datePart = isoOrKey.slice(0, 10);
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return isoOrKey;
    return `${day}/${month}/${year}`;
  }

  /** Etiqueta legible para cada opción del dropdown de corte. */
  formatCutoffOption(item: DehydrationAnalysis): string {
    const labelDate = this.formatCutoffDateDisplay(item.cutoffDateKey || item.cutoffDate);
    const count = item.totalMatched;
    const recordsLabel = count === 1 ? '1 registro elegible' : `${count} registros elegibles`;
    const retained = item.retainedActivitiesCount ?? 0;
    const retainedSuffix =
      retained > 0
        ? ` · ${retained} ${retained === 1 ? 'actividad retenida' : 'actividades retenidas'}`
        : '';
    return `${labelDate} — ${recordsLabel}${retainedSuffix}`;
  }

  /**
   * Paso 3: Descarga el ZIP del corte elegido y guarda el `purgeToken` recibido.
   * Además, dispara la descarga complementaria del archivo .txt en formato Markdown
   * para consulta humana inmediata sin alterar los scripts de rehidratación.
   * El token habilita el botón 'Borrar registros'.
   */
  downloadDehydration(): void {
    const effectiveCutoff = this.dehydrationAnalysis?.cutoffDate || this.cutoffDate;
    if (!effectiveCutoff || this.isDownloadingDehydration) return;

    this.isDownloadingDehydration = true;
    this.errorMessage = null;

    const dateSlug = this.dehydrationAnalysis?.cutoffDateKey || effectiveCutoff.slice(0, 10);
    const filename = `abaq-corte-${dateSlug}.zip`;

    this.backupService.downloadDehydration(effectiveCutoff).subscribe({
      next: (result) => {
        // 1. Descarga del paquete oficial .zip
        this.backupService.triggerDownload(result.blob, filename);
        this.purgeToken = result.purgeToken;
        this.isDownloadingDehydration = false;

        // 2. Descarga complementaria del archivo .txt (Markdown) para lectura del usuario (desfase 300ms)
        setTimeout(() => {
          const txtContent = this.backupService.buildDehydrationMarkdownSummary(
            result.manifest || this.dehydrationAnalysis,
            dateSlug,
          );
          const txtFilename = `abaq-corte-${dateSlug}-resumen.txt`;
          this.backupService.triggerDownloadText(txtContent, txtFilename);
        }, 300);

        if (this.purgeToken) {
          this.showSuccess('Corte oficial (.zip) y resumen de lectura (.txt) descargados correctamente.');
        } else {
          this.showSuccess('Corte descargado, pero no se recibió el token de borrado. Vuelve a descargar antes de purgar.');
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isDownloadingDehydration = false;
        this.errorMessage =
          err?.error?.message || 'Error al descargar el corte de deshidratación.';
        this.cdr.detectChanges();
      },
    });
  }

  // --------------------------------------------------------------------------
  // Métodos de Rehidratación de Cortes Históricos
  // --------------------------------------------------------------------------
  onDehydrationFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (!file.name.endsWith('.zip')) {
        this.errorMessage = 'El archivo de corte a rehidratar debe ser un archivo comprimido .zip.';
        this.selectedDehydrationFile = null;
        input.value = '';
        return;
      }
      this.selectedDehydrationFile = file;
      this.dehydrationDryRunReport = null;
      this.dehydrationRestoreResult = null;
      this.errorMessage = null;
    }
  }

  clearSelectedDehydrationFile(): void {
    this.selectedDehydrationFile = null;
    this.dehydrationDryRunReport = null;
    this.dehydrationRestoreResult = null;
    this.errorMessage = null;
  }

  validateDehydrationRestore(): void {
    if (!this.selectedDehydrationFile) return;

    this.isValidatingDehydrationRestore = true;
    this.errorMessage = null;

    this.backupService.restoreDehydrationPackage(this.selectedDehydrationFile, false).subscribe({
      next: (res) => {
        this.isValidatingDehydrationRestore = false;
        this.dehydrationDryRunReport = res;
        this.showSuccess('Validación preliminar (dry-run) del corte completada. Revisa el balance antes de rehidratar.');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isValidatingDehydrationRestore = false;
        this.errorMessage =
          err?.error?.message || 'El paquete de corte no pasó la validación de integridad.';
        this.cdr.detectChanges();
      },
    });
  }

  openDehydrationConfirmModal(): void {
    this.showDehydrationConfirmModal = true;
  }

  closeDehydrationConfirmModal(): void {
    this.showDehydrationConfirmModal = false;
  }

  executeDehydrationRestore(): void {
    if (!this.selectedDehydrationFile) return;

    this.showDehydrationConfirmModal = false;
    this.isExecutingDehydrationRestore = true;
    this.errorMessage = null;

    this.backupService.restoreDehydrationPackage(this.selectedDehydrationFile, true).subscribe({
      next: (res) => {
        this.isExecutingDehydrationRestore = false;
        this.dehydrationRestoreResult = res;
        this.dehydrationDryRunReport = null;
        this.selectedDehydrationFile = null;
        this.showSuccess(`¡Rehidratación completada exitosamente! ${res.totalRestored || 0} registros restaurados.`);
        this.loadStatus();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isExecutingDehydrationRestore = false;
        this.errorMessage =
          err?.error?.message || 'Ocurrió un error al ejecutar la rehidratación del corte.';
        this.cdr.detectChanges();
      },
    });
  }

  /** Abre la confirmación previa al borrado definitivo del corte. */
  openPurgeConfirmModal(): void {
    if (!this.purgeToken) return;
    this.showPurgeConfirmModal = true;
  }

  /** Cierra la confirmación de borrado. */
  closePurgeConfirmModal(): void {
    this.showPurgeConfirmModal = false;
  }

  /**
   * Paso 4: Ejecuta el borrado definitivo con {cutoffDate, purgeToken}.
   * Solo es alcanzable tras haber descargado el corte (purgeToken presente).
   */
  purgeDehydration(): void {
    const effectiveCutoff = this.dehydrationAnalysis?.cutoffDate || this.cutoffDate;
    if (!effectiveCutoff || !this.purgeToken || this.isPurgingDehydration) return;

    this.showPurgeConfirmModal = false;
    this.isPurgingDehydration = true;
    this.errorMessage = null;

    this.backupService.purgeDehydration(effectiveCutoff, this.purgeToken).subscribe({
      next: () => {
        this.clearEmptyNoticeTimer();
        this.isPurgingDehydration = false;
        // Token consumido: reinicia flujo completo del corte y refresca métricas.
        this.purgeToken = null;
        this.dehydrationAnalysis = null;
        this.availableCutoffs = [];
        this.cutoffDate = '';
        this.analysisState = 'idle';
        this.showSuccess('Registros del corte borrados correctamente. La base de datos ha sido deshidratada.');
        this.loadStatus();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isPurgingDehydration = false;
        this.errorMessage =
          err?.error?.message || 'Ocurrió un error al borrar los registros del corte.';
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Formats raw bytes to human-readable string.
   */
  formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  private showSuccess(msg: string): void {
    this.successMessage = msg;
    setTimeout(() => {
      this.successMessage = null;
      this.cdr.detectChanges();
    }, 6000);
  }
}
